// ============================================================================
// MonthDetailDialog (2026-05-23, extraherad 2026-05-24)
// Klick på månad → modal med topp 10 moms ut (kundfakturor) + topp 10 moms in
// (utlägg + lev.fakturor). Sorterat efter |vatAmount| desc.
// Används av MomsBalanceCard + Overview-sidan.
// ============================================================================
import React, { useEffect, useMemo } from 'react'
import type { CloudReceipt } from '../lib/cloudStore'
import { receiptListTitle } from '../lib/receipts'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']
const fmt = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })

export interface MonthDetailDialogProps {
  month: number          // 0-indexerat (0 = Jan)
  year: number
  companyName: string
  receipts: CloudReceipt[]
  onClose: () => void
}

function getReceiptMonth(r: CloudReceipt): number | null {
  if (r.month != null && r.month >= 0 && r.month <= 11) return r.month
  if (r.date) {
    const d = new Date(r.date)
    if (!isNaN(d.getTime())) return d.getMonth()
  }
  return null
}

function getReceiptVat(r: CloudReceipt): number {
  if (r.vatAmount != null) return r.vatAmount
  if (r.vatRate != null && r.amount != null && r.vatRate > 0) {
    return r.amount * r.vatRate / (1 + r.vatRate)
  }
  return 0
}

export function MonthDetailDialog(props: MonthDetailDialogProps) {
  const { month, year, companyName, receipts, onClose } = props

  const inMonth = useMemo(() => {
    return receipts.filter((r) => getReceiptMonth(r) === month)
  }, [receipts, month])

  const momsUt = useMemo(() => {
    return inMonth
      .filter((r) => r.category === 'kundfakturor')
      .map((r) => ({ r, vat: getReceiptVat(r) }))
      .sort((a, b) => Math.abs(b.vat) - Math.abs(a.vat))
      .slice(0, 10)
  }, [inMonth])

  const momsIn = useMemo(() => {
    return inMonth
      .filter((r) => r.category !== 'kundfakturor')
      .map((r) => ({ r, vat: getReceiptVat(r) }))
      .sort((a, b) => Math.abs(b.vat) - Math.abs(a.vat))
      .slice(0, 10)
  }, [inMonth])

  const totalUt = inMonth.filter((r) => r.category === 'kundfakturor').reduce((s, r) => s + getReceiptVat(r), 0)
  const totalIn = inMonth.filter((r) => r.category !== 'kundfakturor').reduce((s, r) => s + getReceiptVat(r), 0)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    padding: '5vh 1rem', overflowY: 'auto',
  }
  const dialog: React.CSSProperties = {
    background: 'var(--bg-card)', color: 'var(--text)', borderRadius: 8,
    width: 'min(960px, 100%)', border: '1px solid var(--border)',
    boxShadow: '0 12px 48px rgba(0,0,0,0.35)', padding: '1.2rem',
  }
  const colsWrap: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
    gap: '1rem', marginTop: '1rem',
  }
  const colHead: React.CSSProperties = {
    fontWeight: 700, fontSize: '0.95rem', marginBottom: 6, paddingBottom: 4,
    borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between',
  }
  const row: React.CSSProperties = {
    display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0.5rem',
    padding: '0.4rem 0', borderBottom: '1px dashed var(--border)',
    fontSize: '0.82rem', alignItems: 'center',
  }
  const muted: React.CSSProperties = { color: 'var(--text-muted)', fontSize: '0.72rem' }

  function renderList(list: { r: CloudReceipt; vat: number }[], emptyText: string) {
    if (list.length === 0) {
      return <div style={{ ...muted, padding: '0.6rem 0' }}>{emptyText}</div>
    }
    return list.map(({ r, vat }) => {
      const title = receiptListTitle(r) || r.id
      const date = r.date || ''
      const amount = r.amount ?? 0
      const url = r.downloadUrl
      return (
        <div key={r.id} style={row}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={title}>
            {url ? (
              <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text)', textDecoration: 'none' }}>
                {title}
              </a>
            ) : title}
            <div style={muted}>
              {date}{date ? ' · ' : ''}{r.category}{r.accountCode ? ' · ' + r.accountCode : ''}
            </div>
          </div>
          <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', ...muted }}>
            {fmt(amount)}{' kr'}
          </div>
          <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
            {fmt(vat)}{' kr'}
          </div>
        </div>
      )
    })
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={dialog} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{companyName}</div>
            <div style={{ fontSize: '1.15rem', fontWeight: 700 }}>{MONTHS[month] + ' ' + year + ' — topp 10 moms in/ut'}</div>
            <div style={{ ...muted, marginTop: 4 }}>
              {inMonth.length + ' poster denna månad · ' + 'moms ut ' + fmt(totalUt) + ' kr · moms in ' + fmt(totalIn) + ' kr · netto ' + fmt(totalUt - totalIn) + ' kr'}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: '1px solid var(--border)',
              color: 'var(--text)', borderRadius: 4, padding: '0.3rem 0.7rem',
              cursor: 'pointer', fontSize: '0.85rem',
            }}
          >
            {'Stäng (Esc)'}
          </button>
        </div>
        <div style={colsWrap}>
          <div>
            <div style={colHead}>
              <span style={{ color: 'var(--success)' }}>{'↑ Moms ut (kundfakturor)'}</span>
              <span>{fmt(totalUt) + ' kr'}</span>
            </div>
            {renderList(momsUt, 'Inga kundfakturor denna månad')}
          </div>
          <div>
            <div style={colHead}>
              <span style={{ color: 'var(--danger)' }}>{'↓ Moms in (utlägg + lev.fakt)'}</span>
              <span>{fmt(totalIn) + ' kr'}</span>
            </div>
            {renderList(momsIn, 'Inga utlägg/lev.fakturor denna månad')}
          </div>
        </div>
      </div>
    </div>
  )
}
